import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import {
  addFeature,
  addPlan,
  buildFeatureComparisonTranslationFallback,
  cloneFeatureComparisonTranslatedContent,
  createDefaultFeatureComparisonConfig,
  createEmptyFeatureComparisonTranslatedContent,
  normalizeFeatureComparisonConfig,
  removeFeature,
  removePlanAndCleanupFeatures,
  syncFeatureComparisonTranslation,
  validateFeatureComparisonConfig,
} from '../feature-comparison.helpers';
import {
  FeatureComparisonMode,
  FeatureComparisonSimpleConfig,
  FeatureComparisonTranslatedContent,
  FeatureComparisonTranslatedField,
  FeatureComparisonValidationIssue,
  FeatureComparisonValidationResult,
} from '../feature-comparison.types';

type BuilderLanguage = 'es' | 'en';
type GeneralFieldKey = 'title' | 'subtitle' | 'featureColumnTitle';
type BillingFieldKey = 'monthlyLabel' | 'annualLabel';
type PlanTranslatedFieldKey = 'name' | 'description' | 'priceSuffix' | 'redirectLabel';
type PlanBaseFieldKey = 'name' | 'description' | 'monthlyPrice' | 'annualPrice' | 'currency' | 'priceSuffix' | 'redirectLabel' | 'redirectUrl';

@Component({
  selector: 'app-feature-comparison-builder',
  templateUrl: './feature-comparison-builder.component.html',
  styleUrls: ['./feature-comparison-builder.component.scss'],
})
export class FeatureComparisonBuilderComponent implements OnInit, OnChanges {
  @Input() initialConfig?: FeatureComparisonSimpleConfig | null = null;
  @Input() mode: FeatureComparisonMode = 'create';
  @Input() language: BuilderLanguage = 'es';
  @Input() translationConfig?: FeatureComparisonTranslatedContent | null = null;
  @Input() initialTranslationConfig?: FeatureComparisonTranslatedContent | null = null;

  @Output() configChange = new EventEmitter<FeatureComparisonSimpleConfig>();
  @Output() translationChange = new EventEmitter<FeatureComparisonTranslatedContent>();
  @Output() validityChange = new EventEmitter<boolean>();

  workingConfig: FeatureComparisonSimpleConfig = createDefaultFeatureComparisonConfig();
  normalizedConfig: FeatureComparisonSimpleConfig = createDefaultFeatureComparisonConfig();
  normalizedTranslation: FeatureComparisonTranslatedContent = createEmptyFeatureComparisonTranslatedContent();
  validation: FeatureComparisonValidationResult = validateFeatureComparisonConfig(this.normalizedConfig);

  currentStep = 1;
  submitAttempted = false;
  planPendingRemoval: { id: string; name: string } | null = null;
  private hasInitializedFromInput = false;
  private manualDraftValues = new Map<string, string>();

  readonly steps = [
    {
      index: 1 as const,
      labelKey: 'FEATURE_COMPARISON_GENERAL_DATA',
      descriptionKey: 'FEATURE_COMPARISON_GENERAL_DATA_STEP_DESCRIPTION',
    },
    {
      index: 2 as const,
      labelKey: 'FEATURE_COMPARISON_PLANS',
      descriptionKey: 'FEATURE_COMPARISON_PLANS_STEP_DESCRIPTION',
    },
    {
      index: 3 as const,
      labelKey: 'FEATURE_COMPARISON_INCLUDED_FEATURES',
      descriptionKey: 'FEATURE_COMPARISON_INCLUDED_FEATURES_STEP_DESCRIPTION',
    },
    {
      index: 4 as const,
      labelKey: 'FEATURE_COMPARISON_PREVIEW',
      descriptionKey: 'FEATURE_COMPARISON_PREVIEW_STEP_DESCRIPTION',
    },
  ];
  readonly detailMaxLength = 40;

  ngOnInit(): void {
    this.initializeFromInput(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialConfig']) {
      const nextConfig = changes['initialConfig'].currentValue;
      if (!this.hasInitializedFromInput || !this.areConfigsEquivalent(nextConfig, this.workingConfig)) {
        this.initializeFromInput(!this.hasInitializedFromInput);
        return;
      }
    }

    if ((changes['translationConfig'] || changes['initialTranslationConfig'] || changes['language']) && this.hasInitializedFromInput) {
      this.refreshTranslationState(false, this.translationConfig || undefined);
    }
  }

  get isEnglishMode(): boolean {
    return this.language === 'en';
  }

  get plans() {
    return this.normalizedConfig.plans;
  }

  get features() {
    return this.normalizedConfig.features;
  }

  get matrixTableMinWidth(): number | null {
    if (this.plans.length <= 3) {
      return null;
    }

    const featureColumnWidth = 260;
    const planColumnWidth = 160;
    const actionsColumnWidth = this.isEnglishMode ? 0 : 76;
    return featureColumnWidth + (this.plans.length * planColumnWidth) + actionsColumnWidth;
  }

  get previewIssues(): FeatureComparisonValidationIssue[] {
    return this.validation.issues;
  }

  get displayedFeatureColumnTitle(): string {
    return this.isEnglishMode
      ? this.normalizedTranslation.featureColumnTitle.value
      : this.normalizedConfig.featureColumnTitle;
  }

  isCurrentStep(step: number): boolean {
    return this.currentStep === step;
  }

  isStepComplete(step: number): boolean {
    return step < this.currentStep && this.getStepIssues(step).length === 0;
  }

  getStepIssues(step: number): FeatureComparisonValidationIssue[] {
    return this.validation.issues.filter((issue) => issue.step === step);
  }

  hasPathError(path: string): boolean {
    return this.validation.issues.some((issue) => issue.path === path);
  }

  getPathError(path: string): string {
    return this.validation.issues.find((issue) => issue.path === path)?.message || '';
  }

  setStep(step: number): void {
    if (step < this.currentStep) {
      this.currentStep = step;
      return;
    }

    for (let index = this.currentStep; index < step; index += 1) {
      if (this.getStepIssues(index).length > 0) {
        this.currentStep = index;
        this.submitAttempted = true;
        return;
      }
    }

    this.currentStep = step;
  }

  nextStep(): void {
    if (this.getStepIssues(this.currentStep).length > 0) {
      this.submitAttempted = true;
      return;
    }

    this.currentStep = Math.min(this.currentStep + 1, this.steps.length);
  }

  prevStep(): void {
    this.currentStep = Math.max(this.currentStep - 1, 1);
  }

  validateForSubmit(): boolean {
    this.submitAttempted = true;
    this.refreshComputedState(false);

    if (this.validation.isValid) {
      return true;
    }

    this.currentStep = this.validation.issues[0]?.step || 1;
    return false;
  }

  addPlan(): void {
    if (this.isEnglishMode) {
      return;
    }

    this.workingConfig = this.cloneConfig(addPlan(this.normalizedConfig));
    this.refreshComputedState(true);
  }

  removePlan(planId: string): void {
    if (this.isEnglishMode) {
      return;
    }

    const plan = this.plans.find((item) => item.id === planId);
    if (!plan) {
      return;
    }

    this.planPendingRemoval = {
      id: plan.id,
      name: plan.name || 'sin nombre',
    };
  }

  onRemovePlanResponse(confirmed: boolean): void {
    if (!confirmed || !this.planPendingRemoval) {
      this.planPendingRemoval = null;
      return;
    }

    this.workingConfig = this.cloneConfig(removePlanAndCleanupFeatures(this.normalizedConfig, this.planPendingRemoval.id));
    this.planPendingRemoval = null;
    this.refreshComputedState(true);
  }

  getRemovePlanMessage(): string {
    if (!this.planPendingRemoval) {
      return '';
    }

    return `Eliminar el plan "${this.planPendingRemoval.name}" tambien limpiara sus checks. Deseas continuar?`;
  }

  onFeaturedChange(planId: string, checked: boolean): void {
    if (this.isEnglishMode) {
      return;
    }

    this.workingConfig.plans = this.workingConfig.plans.map((plan) => ({
      ...plan,
      featured: checked ? plan.id === planId : plan.id === planId ? false : plan.featured,
    }));
    this.refreshComputedState(true);
  }

  addFeature(): void {
    if (this.isEnglishMode) {
      return;
    }

    this.workingConfig = this.cloneConfig(addFeature(this.normalizedConfig));
    this.refreshComputedState(true);
  }

  removeFeature(featureId: string): void {
    if (this.isEnglishMode) {
      return;
    }

    this.workingConfig = this.cloneConfig(removeFeature(this.normalizedConfig, featureId));
    this.refreshComputedState(true);
  }

  toggleFeatureIncluded(featureId: string, planId: string, checked: boolean): void {
    if (this.isEnglishMode) {
      return;
    }

    const feature = this.workingConfig.features.find((item) => item.id === featureId);
    if (!feature) {
      return;
    }

    feature.includedByPlanId = {
      ...feature.includedByPlanId,
      [planId]: checked,
    };
    feature.detailByPlanId = {
      ...feature.detailByPlanId,
      [planId]: checked ? feature.detailByPlanId?.[planId] || '' : '',
    };
    this.refreshComputedState(true);
  }

  isFeatureIncluded(featureId: string, planId: string): boolean {
    return this.features.find((item) => item.id === featureId)?.includedByPlanId?.[planId] === true;
  }

  getFeatureDetail(featureId: string, planId: string): string {
    return this.features.find((item) => item.id === featureId)?.detailByPlanId?.[planId] || '';
  }

  setFeatureDetail(featureId: string, planId: string, value: string): void {
    if (this.isEnglishMode) {
      return;
    }

    const feature = this.workingConfig.features.find((item) => item.id === featureId);
    if (!feature) {
      return;
    }

    feature.detailByPlanId = {
      ...feature.detailByPlanId,
      [planId]: String(value || '').slice(0, this.detailMaxLength),
    };
    this.refreshComputedState(true);
  }

  getTranslationModeLabelKey(isManual: boolean): string {
    return isManual ? 'FEATURE_COMPARISON_TRANSLATION_MANUAL' : 'FEATURE_COMPARISON_TRANSLATION_AUTOMATIC';
  }

  getGeneralFieldValue(field: GeneralFieldKey): string {
    return this.isEnglishMode
      ? this.normalizedTranslation[field].value
      : this.workingConfig[field];
  }

  setGeneralFieldValue(field: GeneralFieldKey, value: string): void {
    if (this.isEnglishMode) {
      const nextValue = value || '';
      this.normalizedTranslation = {
        ...this.normalizedTranslation,
        [field]: {
          ...this.normalizedTranslation[field],
          value: nextValue,
        },
      };
      this.storeManualDraft(this.getGeneralDraftKey(field), this.normalizedTranslation[field]);
      this.refreshTranslationState(true);
      return;
    }

    this.workingConfig[field] = value || '';
    this.refreshComputedState(true);
  }

  isGeneralFieldManual(field: GeneralFieldKey): boolean {
    return this.normalizedTranslation[field].manual;
  }

  toggleGeneralField(field: GeneralFieldKey, manual: boolean): void {
    if (!this.isEnglishMode) {
      return;
    }

    const fallback = buildFeatureComparisonTranslationFallback(this.normalizedConfig);
    const currentField = this.normalizedTranslation[field];
    const initialField = this.initialTranslationState[field];
    const draftKey = this.getGeneralDraftKey(field);

    this.normalizedTranslation = {
      ...this.normalizedTranslation,
      [field]: this.buildTranslatedFieldState(currentField, initialField, fallback[field].value, manual, draftKey),
    };
    this.refreshTranslationState(true);
  }

  getBillingFieldValue(field: BillingFieldKey): string {
    return this.isEnglishMode
      ? this.normalizedTranslation.billing[field].value
      : this.workingConfig.billing[field];
  }

  setBillingFieldValue(field: BillingFieldKey, value: string): void {
    if (this.isEnglishMode) {
      const nextValue = value || '';
      this.normalizedTranslation = {
        ...this.normalizedTranslation,
        billing: {
          ...this.normalizedTranslation.billing,
          [field]: {
            ...this.normalizedTranslation.billing[field],
            value: nextValue,
          },
        },
      };
      this.storeManualDraft(this.getBillingDraftKey(field), this.normalizedTranslation.billing[field]);
      this.refreshTranslationState(true);
      return;
    }

    this.workingConfig.billing = {
      ...this.workingConfig.billing,
      [field]: value || '',
    };
    this.refreshComputedState(true);
  }

  setPriceDisplayMode(value: string): void {
    if (this.isEnglishMode) {
      return;
    }

    this.workingConfig.billing = {
      ...this.workingConfig.billing,
      priceDisplayMode: value === 'annual' || value === 'toggle' ? value : 'monthly',
    };
    this.refreshComputedState(true);
  }

  setBillingDefaultMode(value: string): void {
    if (this.isEnglishMode) {
      return;
    }

    this.workingConfig.billing = {
      ...this.workingConfig.billing,
      defaultMode: value === 'annual' ? 'annual' : 'monthly',
    };
    this.refreshComputedState(true);
  }

  isBillingFieldManual(field: BillingFieldKey): boolean {
    return this.normalizedTranslation.billing[field].manual;
  }

  toggleBillingField(field: BillingFieldKey, manual: boolean): void {
    if (!this.isEnglishMode) {
      return;
    }

    const fallback = buildFeatureComparisonTranslationFallback(this.normalizedConfig);
    const currentField = this.normalizedTranslation.billing[field];
    const initialField = this.initialTranslationState.billing[field];
    const draftKey = this.getBillingDraftKey(field);

    this.normalizedTranslation = {
      ...this.normalizedTranslation,
      billing: {
        ...this.normalizedTranslation.billing,
        [field]: this.buildTranslatedFieldState(currentField, initialField, fallback.billing[field].value, manual, draftKey),
      },
    };
    this.refreshTranslationState(true);
  }

  getPlanTranslatedField(planId: string, field: PlanTranslatedFieldKey): string {
    const translatedPlan = this.normalizedTranslation.plans.find((plan) => plan.id === planId);
    return translatedPlan?.[field]?.value || '';
  }

  setPlanTranslatedField(planId: string, field: PlanTranslatedFieldKey, value: string): void {
    if (!this.isEnglishMode) {
      return;
    }

    const nextValue = value || '';
    this.normalizedTranslation = {
      ...this.normalizedTranslation,
      plans: this.normalizedTranslation.plans.map((plan) =>
        plan.id === planId
          ? {
            ...plan,
            [field]: {
              ...plan[field],
              value: nextValue,
            },
          }
          : plan
      ),
    };
    const updatedPlan = this.normalizedTranslation.plans.find((plan) => plan.id === planId);
    this.storeManualDraft(this.getPlanDraftKey(planId, field), updatedPlan?.[field]);
    this.refreshTranslationState(true);
  }

  isPlanFieldManual(planId: string, field: PlanTranslatedFieldKey): boolean {
    return this.normalizedTranslation.plans.find((plan) => plan.id === planId)?.[field]?.manual === true;
  }

  togglePlanField(planId: string, field: PlanTranslatedFieldKey, manual: boolean): void {
    if (!this.isEnglishMode) {
      return;
    }

    const fallbackPlan = buildFeatureComparisonTranslationFallback(this.normalizedConfig).plans.find((plan) => plan.id === planId);
    const initialPlan = this.initialTranslationState.plans.find((plan) => plan.id === planId);
    const draftKey = this.getPlanDraftKey(planId, field);

    this.normalizedTranslation = {
      ...this.normalizedTranslation,
      plans: this.normalizedTranslation.plans.map((plan) =>
        plan.id === planId
          ? {
            ...plan,
            [field]: this.buildTranslatedFieldState(
              plan[field],
              initialPlan?.[field],
              fallbackPlan?.[field].value || '',
              manual,
              draftKey
            ),
          }
          : plan
      ),
    };
    this.refreshTranslationState(true);
  }

  setBasePlanField(planId: string, field: PlanBaseFieldKey, value: string): void {
    if (this.isEnglishMode) {
      return;
    }

    this.workingConfig.plans = this.workingConfig.plans.map((plan) =>
      plan.id === planId ? { ...plan, [field]: value || '' } : plan
    );
    this.refreshComputedState(true);
  }

  getFeatureTranslatedField(featureId: string): string {
    return this.normalizedTranslation.features.find((feature) => feature.id === featureId)?.label.value || '';
  }

  setFeatureTranslatedField(featureId: string, value: string): void {
    if (!this.isEnglishMode) {
      return;
    }

    const nextValue = value || '';
    this.normalizedTranslation = {
      ...this.normalizedTranslation,
      features: this.normalizedTranslation.features.map((feature) =>
        feature.id === featureId
          ? {
            ...feature,
            label: {
              ...feature.label,
              value: nextValue,
            },
          }
          : feature
      ),
    };
    const updatedFeature = this.normalizedTranslation.features.find((feature) => feature.id === featureId);
    this.storeManualDraft(this.getFeatureDraftKey(featureId), updatedFeature?.label);
    this.refreshTranslationState(true);
  }

  getFeatureTranslatedDetail(featureId: string, planId: string): string {
    return this.normalizedTranslation.features.find((feature) => feature.id === featureId)
      ?.detailByPlanId?.[planId]?.value || '';
  }

  setFeatureTranslatedDetail(featureId: string, planId: string, value: string): void {
    if (!this.isEnglishMode || !this.isFeatureIncluded(featureId, planId)) {
      return;
    }

    const nextValue = String(value || '').slice(0, this.detailMaxLength);
    this.normalizedTranslation = {
      ...this.normalizedTranslation,
      features: this.normalizedTranslation.features.map((feature) =>
        feature.id === featureId
          ? {
            ...feature,
            detailByPlanId: {
              ...feature.detailByPlanId,
              [planId]: {
                ...feature.detailByPlanId[planId],
                value: nextValue,
              },
            },
          }
          : feature
      ),
    };
    const updatedFeature = this.normalizedTranslation.features.find((feature) => feature.id === featureId);
    this.storeManualDraft(this.getFeatureDetailDraftKey(featureId, planId), updatedFeature?.detailByPlanId?.[planId]);
    this.refreshTranslationState(true);
  }

  isFeatureDetailManual(featureId: string, planId: string): boolean {
    return this.normalizedTranslation.features.find((feature) => feature.id === featureId)
      ?.detailByPlanId?.[planId]?.manual === true;
  }

  toggleFeatureDetail(featureId: string, planId: string, manual: boolean): void {
    if (!this.isEnglishMode || !this.isFeatureIncluded(featureId, planId)) {
      return;
    }

    const fallbackFeature = buildFeatureComparisonTranslationFallback(this.normalizedConfig)
      .features.find((feature) => feature.id === featureId);
    const initialFeature = this.initialTranslationState.features.find((feature) => feature.id === featureId);
    const draftKey = this.getFeatureDetailDraftKey(featureId, planId);

    this.normalizedTranslation = {
      ...this.normalizedTranslation,
      features: this.normalizedTranslation.features.map((feature) =>
        feature.id === featureId
          ? {
            ...feature,
            detailByPlanId: {
              ...feature.detailByPlanId,
              [planId]: this.buildTranslatedFieldState(
                feature.detailByPlanId[planId],
                initialFeature?.detailByPlanId?.[planId],
                fallbackFeature?.detailByPlanId?.[planId]?.value || '',
                manual,
                draftKey
              ),
            },
          }
          : feature
      ),
    };
    this.refreshTranslationState(true);
  }

  isFeatureFieldManual(featureId: string): boolean {
    return this.normalizedTranslation.features.find((feature) => feature.id === featureId)?.label.manual === true;
  }

  toggleFeatureField(featureId: string, manual: boolean): void {
    if (!this.isEnglishMode) {
      return;
    }

    const fallbackFeature = buildFeatureComparisonTranslationFallback(this.normalizedConfig).features.find((feature) => feature.id === featureId);
    const initialFeature = this.initialTranslationState.features.find((feature) => feature.id === featureId);
    const draftKey = this.getFeatureDraftKey(featureId);

    this.normalizedTranslation = {
      ...this.normalizedTranslation,
      features: this.normalizedTranslation.features.map((feature) =>
        feature.id === featureId
          ? {
            ...feature,
            label: this.buildTranslatedFieldState(
              feature.label,
              initialFeature?.label,
              fallbackFeature?.label.value || '',
              manual,
              draftKey
            ),
          }
          : feature
      ),
    };
    this.refreshTranslationState(true);
  }

  setBaseFeatureLabel(featureId: string, value: string): void {
    if (this.isEnglishMode) {
      return;
    }

    this.workingConfig.features = this.workingConfig.features.map((feature) =>
      feature.id === featureId ? { ...feature, label: value || '' } : feature
    );
    this.refreshComputedState(true);
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  private get initialTranslationState(): FeatureComparisonTranslatedContent {
    const fallback = buildFeatureComparisonTranslationFallback(this.normalizedConfig);
    return this.initialTranslationConfig
      ? syncFeatureComparisonTranslation(this.normalizedConfig, this.initialTranslationConfig, this.initialTranslationConfig)
      : fallback;
  }

  private initializeFromInput(resetStep: boolean): void {
    const baseConfig = this.initialConfig ? normalizeFeatureComparisonConfig(this.initialConfig) : createDefaultFeatureComparisonConfig();
    this.workingConfig = this.cloneConfig(baseConfig);
    if (resetStep) {
      this.currentStep = 1;
      this.submitAttempted = false;
    }
    this.hasInitializedFromInput = true;
    this.refreshComputedState(false);
  }

  refreshComputedState(emitConfig: boolean): void {
    this.normalizedConfig = normalizeFeatureComparisonConfig(this.workingConfig);
    this.validation = validateFeatureComparisonConfig(this.normalizedConfig);
    this.refreshTranslationState(false);
    if (emitConfig) {
      this.configChange.emit(this.cloneConfig(this.normalizedConfig));
    }
    this.validityChange.emit(this.validation.isValid);
  }

  private refreshTranslationState(
    emitTranslation: boolean,
    sourceTranslationOverride?: FeatureComparisonTranslatedContent | null
  ): void {
    const fallback = buildFeatureComparisonTranslationFallback(this.normalizedConfig);
    const sourceTranslation = sourceTranslationOverride
      ? cloneFeatureComparisonTranslatedContent(sourceTranslationOverride)
      : this.hasInitializedFromInput
        ? cloneFeatureComparisonTranslatedContent(this.normalizedTranslation)
        : this.translationConfig
          ? cloneFeatureComparisonTranslatedContent(this.translationConfig)
          : fallback;

    this.normalizedTranslation = syncFeatureComparisonTranslation(
      this.normalizedConfig,
      sourceTranslation,
      this.initialTranslationConfig || sourceTranslation
    );
    this.pruneManualDrafts();
    this.seedManualDrafts(this.normalizedTranslation);

    if (emitTranslation) {
      this.translationChange.emit(cloneFeatureComparisonTranslatedContent(this.normalizedTranslation));
    }
  }

  private buildTranslatedFieldState(
    currentField: FeatureComparisonTranslatedField,
    initialField: FeatureComparisonTranslatedField | undefined,
    fallbackValue: string,
    manual: boolean,
    draftKey: string
  ): FeatureComparisonTranslatedField {
    if (manual) {
      const value = this.manualDraftValues.has(draftKey)
        ? this.manualDraftValues.get(draftKey) || ''
        : initialField?.manual
          ? initialField.value || ''
          : currentField?.value || '';

      return {
        value,
        manual: true,
      };
    }

    this.storeManualDraft(draftKey, currentField);

    const automaticValue =
      currentField && !currentField.manual && String(currentField.value || '').trim()
        ? String(currentField.value || '').trim()
        : initialField && !initialField.manual && String(initialField.value || '').trim()
          ? String(initialField.value || '').trim()
          : String(fallbackValue || '').trim();

    return {
      value: automaticValue,
      manual: false,
    };
  }

  private storeManualDraft(key: string, field: FeatureComparisonTranslatedField | undefined): void {
    if (!field?.manual) {
      return;
    }

    this.manualDraftValues.set(key, field.value || '');
  }

  private seedManualDrafts(translation: FeatureComparisonTranslatedContent): void {
    this.storeManualDraft(this.getGeneralDraftKey('title'), translation.title);
    this.storeManualDraft(this.getGeneralDraftKey('subtitle'), translation.subtitle);
    this.storeManualDraft(this.getGeneralDraftKey('featureColumnTitle'), translation.featureColumnTitle);
    this.storeManualDraft(this.getBillingDraftKey('monthlyLabel'), translation.billing.monthlyLabel);
    this.storeManualDraft(this.getBillingDraftKey('annualLabel'), translation.billing.annualLabel);

    translation.plans.forEach((plan) => {
      this.storeManualDraft(this.getPlanDraftKey(plan.id, 'name'), plan.name);
      this.storeManualDraft(this.getPlanDraftKey(plan.id, 'description'), plan.description);
      this.storeManualDraft(this.getPlanDraftKey(plan.id, 'priceSuffix'), plan.priceSuffix);
      this.storeManualDraft(this.getPlanDraftKey(plan.id, 'redirectLabel'), plan.redirectLabel);
    });

    translation.features.forEach((feature) => {
      this.storeManualDraft(this.getFeatureDraftKey(feature.id), feature.label);
      Object.keys(feature.detailByPlanId || {}).forEach((planId) => {
        this.storeManualDraft(
          this.getFeatureDetailDraftKey(feature.id, planId),
          feature.detailByPlanId[planId]
        );
      });
    });
  }

  private pruneManualDrafts(): void {
    const validDraftKeys = new Set<string>([
      this.getGeneralDraftKey('title'),
      this.getGeneralDraftKey('subtitle'),
      this.getGeneralDraftKey('featureColumnTitle'),
      this.getBillingDraftKey('monthlyLabel'),
      this.getBillingDraftKey('annualLabel'),
    ]);

    this.normalizedConfig.plans.forEach((plan) => {
      validDraftKeys.add(this.getPlanDraftKey(plan.id, 'name'));
      validDraftKeys.add(this.getPlanDraftKey(plan.id, 'description'));
      validDraftKeys.add(this.getPlanDraftKey(plan.id, 'priceSuffix'));
      validDraftKeys.add(this.getPlanDraftKey(plan.id, 'redirectLabel'));
    });

    this.normalizedConfig.features.forEach((feature) => {
      validDraftKeys.add(this.getFeatureDraftKey(feature.id));
      this.normalizedConfig.plans.forEach((plan) => {
        if (feature.includedByPlanId?.[plan.id] === true) {
          validDraftKeys.add(this.getFeatureDetailDraftKey(feature.id, plan.id));
        }
      });
    });

    Array.from(this.manualDraftValues.keys()).forEach((key) => {
      if (!validDraftKeys.has(key)) {
        this.manualDraftValues.delete(key);
      }
    });
  }

  private getGeneralDraftKey(field: GeneralFieldKey): string {
    return `general:${field}`;
  }

  private getBillingDraftKey(field: BillingFieldKey): string {
    return `billing:${field}`;
  }

  private getPlanDraftKey(planId: string, field: PlanTranslatedFieldKey): string {
    return `plan:${planId}:${field}`;
  }

  private getFeatureDraftKey(featureId: string): string {
    return `feature:${featureId}:label`;
  }

  private getFeatureDetailDraftKey(featureId: string, planId: string): string {
    return `feature:${featureId}:detail:${planId}`;
  }

  private cloneConfig(config: FeatureComparisonSimpleConfig): FeatureComparisonSimpleConfig {
    return JSON.parse(JSON.stringify(config));
  }

  private areConfigsEquivalent(nextConfig: FeatureComparisonSimpleConfig | null | undefined, currentConfig: FeatureComparisonSimpleConfig): boolean {
    if (!nextConfig) {
      return false;
    }

    return JSON.stringify(normalizeFeatureComparisonConfig(nextConfig)) === JSON.stringify(normalizeFeatureComparisonConfig(currentConfig));
  }
}
